/**
 * LockedPage — full-page splash shown when the active plan doesn't
 * include the gating feature for an entire route (e.g. Reportes,
 * Escenarios).
 *
 * iter-170ac: introduced.
 * iter-170ad: auto-fetches the feature label + plan/addon/pack
 *             recommendation from the backend via
 *             ``useFeatureRecommendation``. Bullets stay caller-driven
 *             so each page can sell its own value prop.
 */
import { Layout } from "./Layout";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Link } from "react-router-dom";
import { Lock, Sparkles } from "lucide-react";
import { useLang } from "../contexts/LangContext";
import { tx } from "../i18n";
import { useFeatureRecommendation } from "../hooks/useFeatureRecommendation";
import { RecommendationCards } from "./RecommendationCards";

export function LockedPage({ featureKey, titleEs, titleEn, titlePt, bullets = [] }) {
  const { lang } = useLang();
  const { data: rec } = useFeatureRecommendation(featureKey);
  // Title resolution: explicit override wins (some pages want a
  // marketing-y headline like "Escenarios financieros what-if"), else
  // fall back to the catalog label so we don't have to hardcode copy.
  const resolvedTitle = tx(
    lang,
    titleEn || rec?.feature?.label_en || featureKey,
    titleEs || rec?.feature?.label_es || featureKey,
    titlePt || rec?.feature?.label_pt || featureKey,
  );

  return (
    <Layout>
      <div className="max-w-3xl mx-auto py-12" data-testid={`locked-page-${featureKey}`}>
        <Card className="p-8 rounded-2xl border-sky-200 bg-gradient-to-br from-sky-50 to-white">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-full bg-sky-100 text-sky-700 flex items-center justify-center flex-shrink-0">
              <Lock className="h-6 w-6" />
            </div>
            <div className="flex-1 space-y-4">
              <div>
                <h1 className="font-display text-2xl font-bold text-slate-900">{resolvedTitle}</h1>
                <p className="text-sm text-slate-600 leading-snug mt-1">
                  {tx(lang,
                    "Your current plan doesn't include this feature.",
                    "Tu plan actual no incluye esta funcionalidad.",
                    "Seu plano atual não inclui esta funcionalidade.")}
                </p>
              </div>
              {bullets.length > 0 && (
                <ul className="space-y-1.5 text-sm" data-testid={`locked-page-bullets-${featureKey}`}>
                  {bullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Sparkles className="h-3.5 w-3.5 text-sky-600 shrink-0 mt-0.5" />
                      <span className="text-slate-700">{tx(lang, b.en, b.es, b.pt)}</span>
                    </li>
                  ))}
                </ul>
              )}
              {/* iter-170ad — Concrete upgrade paths: plan / addon / pack.
                  Picked from the catalog by ``recommendations_for_feature``
                  so users see the cheapest of each kind, in their currency. */}
              <div className="pt-2">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                  {tx(lang, "Unlock with", "Activala con", "Ative com")}
                </p>
                <RecommendationCards featureKey={featureKey} />
              </div>
              <div className="flex flex-wrap gap-2 pt-2">
                <Link to="/">
                  <Button
                    variant="outline"
                    className="rounded-full"
                    data-testid={`locked-page-back-${featureKey}`}
                  >
                    {tx(lang, "Back to dashboard", "Volver al dashboard", "Voltar ao dashboard")}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </Layout>
  );
}
